import { createAsyncThunk } from '@reduxjs/toolkit'
import { translate as translateBase } from 'loankeeper-common/helpers'
import { CRAStatusServerData, CRAProfileServerData, AddCRAClientServerData, CRAStatus } from '@lk/api'

import { actions as LKModalActions } from '../../features/shared/LKModal'
import { ThunkApi } from '../index.d'

const translate = (key) => translateBase(`LKMESSAGE-${key}`)

interface CRADataPayload {
  CRARequestID: number
  HashSum_CRARequestID_: string
  profile: CRAProfileServerData
}

export interface CRARequestAttributes {
  pin: string
  idDocNo: number
  birthYear: number
  subContractId: number
}

export const loadCRAData = createAsyncThunk<CRADataPayload, CRARequestAttributes, ThunkApi>(
  '[CRA] LOAD_CRA_DATA',
  async ({ pin, idDocNo, birthYear, subContractId }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.cra.get(pin, idDocNo, birthYear, subContractId)

    if (!result.success) {
      dispatch(LKModalActions.openError(translate(result.error.message)))

      return rejectWithValue('Failed to request CRA')
    }

    let statusPayload: CRAStatusServerData

    while (true) {
      const statusResult = await extra.api.cra.getStatus(result.data.SDACRAIdent)

      if (!statusResult.success) {
        dispatch(LKModalActions.openError(translate(statusResult.error.message)))
        return rejectWithValue('Failed to fetch CRA status')
      }

      const { StatusID, Data, Error } = statusResult.data

      if (pin !== Data.CardNumber) {
        dispatch(LKModalActions.openError(translate(Error.Message || Error.Text || 'ERR_SDA_CRA_REQUEST_FAILED')))
        return rejectWithValue('Personal numbers do not match')
      }

      if (StatusID === CRAStatus.Success) {
        statusPayload = statusResult.data
        break
      }
    }

    const {
      Data: { CRARequestID, HashSum_CRARequestID_ },
    } = statusPayload

    if (!CRARequestID) {
      dispatch(LKModalActions.openError(translate('HINT_ERR_TITLE')))
      return rejectWithValue('Missing "CRARequestID"')
    }

    const craDataResult = await extra.api.cra.getData(CRARequestID)

    if (!craDataResult.success) {
      dispatch(LKModalActions.openError(translate(craDataResult.error.message)))
      return rejectWithValue('Failed to fetch CRA data')
    }

    return {
      CRARequestID,
      HashSum_CRARequestID_,
      profile: craDataResult.data,
    }
  },
)

interface AddCRAClientAttributes {
  pin: string
  requestId: number
  requestHash: string
  clientId: number
  clientHash: string
}

export const addCRAClient = createAsyncThunk<AddCRAClientServerData, AddCRAClientAttributes, ThunkApi>(
  '[CRA] ADD_CRA_CLIENT',
  async ({ pin, requestId, requestHash, clientId, clientHash }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.cra.add(pin, requestId, requestHash, clientId, clientHash)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to add CRA client')
  },
)

export const printCRA = createAsyncThunk<unknown, Record<string, string>, ThunkApi>(
  '[CRA] PRINT_CRA_CLIENT',
  async ({ action }, { dispatch, extra, rejectWithValue }) => {
    const result = await extra.api.cra.printCRA(action)

    if (result.success) {
      return result.data
    }

    dispatch(LKModalActions.openError(translate(result.error.message)))
    return rejectWithValue('Failed to add CRA client')
  },
)
